import React, { useEffect, useRef } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BrandMark } from '../../components/ui/BrandMark';
import { GlassButton } from '../../components/ui/GlassButton';
import { GlassCard } from '../../components/ui/GlassCard';
import { TabIcon } from '../../components/ui/icons/TabIcon';
import { APP_NAME } from '../../config/branding';
import { useAuth } from '../../contexts/AuthContext';
import {
  navigateToLanding,
  navigateToLegal,
  openDashboard,
  openExplore,
  openWebAppSignIn,
} from '../../core/navigation/navigationRef';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useAppLayout } from '../../hooks/useAppLayout';
import { useBillingActions } from '../../hooks/useBillingActions';
import { CapabilityShot } from './CapabilityShots';
import { SeoExploreLinks } from './SeoExploreLinks';
import {
  APP_STORE_URL,
  CAPABILITIES,
  FREE_FEATURES,
  FREE_LIMITS,
  HOW_STEPS,
  LANDING_APP_LEAD,
  LANDING_APP_TITLE,
  LANDING_HERO_BODY,
  LANDING_HERO_LINE,
  LANDING_HERO_PILLARS,
  LANDING_HOW_TITLE,
  LANDING_PRODUCT_TITLE,
  LANDING_NAV_ANCHORS,
  LANDING_PRO_PRICE_USD,
  PLAN_COMPARE,
  PLATFORM_POINTS,
  PLAY_STORE_URL,
  PRO_FEATURES,
} from './landingCopy';
import {
  getLandingHash,
  landingHref,
  setLandingHash,
  subscribeLandingHash,
  type LandingSectionId,
} from './landingAnchors';
import { isIosPhoneBrowser } from './isIosPhoneBrowser';
import { openStoreUrl } from './openStoreUrl';
import { StoreBadges } from './StoreBadges';

type SectionId = LandingSectionId;
const isWeb = Platform.OS === ('web' as typeof Platform.OS);

const TAB_SHOT_ICON: Record<string, 'spend' | 'wealth' | 'grow' | 'loan' | 'home'> = {
  spend: 'spend',
  wealth: 'wealth',
  grow: 'grow',
  loan: 'loan',
  import: 'home',
  cards: 'spend',
  household: 'home',
  alerts: 'spend',
  ai: 'home',
};

export function LandingScreen() {
  const { width } = useAppLayout();
  const insets = useSafeAreaInsets();
  const { isAuthenticated, user } = useAuth();
  const { upgradeToPro, busy: billingBusy } = useBillingActions();
  const compact = width < 768;
  const twoCol = width >= 1024;
  const showStoreBadges = !isIosPhoneBrowser();
  const scrollRef = useRef<ScrollView>(null);
  const sectionY = useRef<Partial<Record<SectionId, number>>>({});
  const pendingHash = useRef<SectionId | null>(isWeb ? getLandingHash() : null);
  const isPro = user?.plan === 'pro';

  const scrollToSection = (id: SectionId) => {
    const y = sectionY.current[id];
    if (y == null) return false;
    const offset = compact ? 8 : 88;
    scrollRef.current?.scrollTo({ y: Math.max(0, y - offset), animated: true });
    return true;
  };

  const scrollTo = (id: SectionId) => {
    if (isWeb) {
      setLandingHash(id);
    }
    if (!scrollToSection(id)) {
      pendingHash.current = id;
    }
  };

  const onSectionLayout = (id: SectionId, y: number) => {
    sectionY.current[id] = y;
    if (pendingHash.current === id && scrollToSection(id)) {
      pendingHash.current = null;
    }
  };

  useEffect(() => {
    if (!isWeb) return;
    return subscribeLandingHash(() => {
      const id = getLandingHash();
      if (!id) return;
      if (!scrollToSection(id)) {
        pendingHash.current = id;
      }
    });
  }, [compact]);

  const onSignIn = () => (isAuthenticated ? openDashboard() : openWebAppSignIn());
  const onExplore = () => openExplore();
  const onProCta = () => {
    if (!isAuthenticated) {
      openWebAppSignIn();
      return;
    }
    if (isPro) {
      openDashboard();
      return;
    }
    void upgradeToPro('landing');
  };

  return (
    <View style={styles.root}>
      {compact ? null : (
        <View pointerEvents="none" style={[styles.statusScrim, { height: insets.top }]} />
      )}
      <LandingNav
        compact={compact}
        isAuthenticated={isAuthenticated}
        onAnchor={scrollTo}
        onSignIn={onSignIn}
        topInset={insets.top}
      />
      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          compact ? styles.scrollContentCompact : { paddingTop: 64 + insets.top },
          { paddingBottom: spacing.xl * 2 + insets.bottom },
        ]}
        showsVerticalScrollIndicator={false}
        contentInsetAdjustmentBehavior="never"
      >
        <View style={styles.hero} accessibilityRole="header">
          <Text style={styles.kicker}>{APP_NAME}</Text>
          <Text style={[styles.heroTitle, compact && styles.heroTitleCompact]}>
            {LANDING_HERO_LINE}
          </Text>
          <Text style={[styles.heroBody, compact && styles.heroBodyCompact]}>{LANDING_HERO_BODY}</Text>
          <View style={styles.heroPillars}>
            {LANDING_HERO_PILLARS.map((pillar) => (
              <Text key={pillar} style={styles.heroPillar}>
                {pillar}
              </Text>
            ))}
          </View>
          <View style={styles.heroCtas}>
            <GlassButton variant="primary" onPress={onSignIn}>
              {isAuthenticated ? 'Go to dashboard' : 'Sign in'}
            </GlassButton>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Explore sample dashboard"
              onPress={onExplore}
              style={({ pressed }) => [styles.exploreLink, pressed && styles.pressed]}
            >
              <Text style={styles.exploreLinkText}>Explore sample</Text>
            </Pressable>
          </View>
          {showStoreBadges ? <StoreBadges /> : null}
        </View>

        <View
          nativeID="product"
          onLayout={(e) => {
            onSectionLayout('product', e.nativeEvent.layout.y);
          }}
          style={styles.section}
        >
          <Text style={styles.sectionEyebrow}>Capabilities</Text>
          <Text style={[styles.sectionTitle, compact && styles.sectionTitleCompact]}>
            {LANDING_PRODUCT_TITLE}
          </Text>
          <View style={[styles.grid, twoCol && styles.gridTwo, !twoCol && compact && styles.gridOne]}>
            {CAPABILITIES.map((cap) => (
              <View
                key={cap.id}
                style={[styles.gridCell, 'featured' in cap && cap.featured && styles.gridCellFeatured]}
              >
                <GlassCard style={[styles.capCard, compact && styles.capCardCompact]}>
                  <View style={styles.capHead}>
                    <TabIcon
                      name={TAB_SHOT_ICON[cap.id] ?? 'home'}
                      color={colors.accent}
                      focused
                      size={18}
                    />
                    <Text style={styles.capTitle}>{cap.title}</Text>
                    {'badge' in cap && cap.badge ? (
                      <Text style={styles.proBadge}>{cap.badge}</Text>
                    ) : null}
                  </View>
                  <Text style={styles.capLine}>{cap.line}</Text>
                  <Text style={styles.capWhy}>{cap.why}</Text>
                  <View style={styles.shotFrame}>
                    <CapabilityShot id={cap.shot} compact={compact} />
                  </View>
                </GlassCard>
              </View>
            ))}
          </View>
        </View>

        <View
          nativeID="how"
          onLayout={(e) => {
            onSectionLayout('how', e.nativeEvent.layout.y);
          }}
          style={styles.section}
        >
          <Text style={styles.sectionEyebrow}>How it works</Text>
          <Text style={[styles.sectionTitle, compact && styles.sectionTitleCompact]}>
            {LANDING_HOW_TITLE}
          </Text>
          <View style={[styles.howRow, compact && styles.howCol]}>
            {HOW_STEPS.map((step) => (
              <View key={step.n} style={styles.howCard}>
                <Text style={styles.howN}>{step.n}</Text>
                <Text style={styles.howTitle}>{step.title}</Text>
                <Text style={styles.howBody}>{step.body}</Text>
              </View>
            ))}
          </View>
        </View>

        <View
          nativeID="app"
          onLayout={(e) => {
            onSectionLayout('app', e.nativeEvent.layout.y);
          }}
          style={styles.section}
        >
          <Text style={styles.sectionEyebrow}>Get the app</Text>
          <Text style={[styles.sectionTitle, compact && styles.sectionTitleCompact]}>
            {LANDING_APP_TITLE}
          </Text>
          <Text style={styles.sectionLead}>{LANDING_APP_LEAD}</Text>
          {showStoreBadges ? <StoreBadges /> : null}
          <View style={[styles.howRow, compact && styles.howCol]}>
            {PLATFORM_POINTS.map((point) => (
              <View key={point.id} style={styles.howCard}>
                <Text style={styles.howTitle}>{point.title}</Text>
                <Text style={styles.howBody}>{point.body}</Text>
              </View>
            ))}
          </View>
        </View>

        <View
          nativeID="pricing"
          onLayout={(e) => {
            onSectionLayout('pricing', e.nativeEvent.layout.y);
          }}
          style={styles.section}
        >
          <Text style={styles.sectionEyebrow}>Pricing</Text>
          <Text style={[styles.sectionTitle, compact && styles.sectionTitleCompact]}>
            Free to see it. Pro to act on it.
          </Text>
          <Text style={styles.sectionLead}>
            Free is the dashboard and five screenshot imports — card, bank, or brokerage. Pro is
            chat, spend and card-due alerts you set, and room to keep uploading.
          </Text>
          <View style={[styles.priceRow, compact && styles.howCol]}>
            <GlassCard style={styles.priceCard}>
              <Text style={styles.priceName}>Free</Text>
              <Text style={styles.priceAmount}>$0</Text>
              <Text style={styles.priceCadence}>to explore and import</Text>
              {FREE_FEATURES.map((item) => (
                <Text key={item} style={styles.priceFeature}>
                  {item}
                </Text>
              ))}
              {FREE_LIMITS.map((item) => (
                <Text key={item} style={styles.priceLimit}>
                  {item}
                </Text>
              ))}
              <View style={styles.priceCta}>
                <GlassButton variant="secondary" fullWidth onPress={onSignIn}>
                  {isAuthenticated ? 'Go to dashboard' : 'Sign in'}
                </GlassButton>
              </View>
            </GlassCard>

            <View style={styles.priceCardProWrap}>
            <GlassCard style={styles.priceCard}>
              <Text style={styles.priceName}>Pro</Text>
              <Text style={styles.priceAmount}>${LANDING_PRO_PRICE_USD}</Text>
              <Text style={styles.priceCadence}>per month</Text>
              {PRO_FEATURES.map((item) => (
                <Text key={item} style={styles.priceFeature}>
                  {item}
                </Text>
              ))}
              <View style={styles.priceCta}>
                <GlassButton
                  variant="primary"
                  fullWidth
                  disabled={billingBusy}
                  onPress={onProCta}
                >
                  {isPro ? 'Open app' : isAuthenticated ? 'Subscribe' : 'Start with Pro'}
                </GlassButton>
              </View>
            </GlassCard>
            </View>
          </View>
          <View style={styles.compare}>
            <View style={styles.compareHead}>
              <Text style={styles.compareHeadSpacer} />
              <Text style={[styles.compareHeadFree, compact && styles.compareColCompact]}>
                Free
              </Text>
              <Text style={[styles.compareHeadPro, compact && styles.compareColCompact]}>Pro</Text>
            </View>
            {PLAN_COMPARE.map((row) => (
              <View key={row.label} style={styles.compareRow}>
                <Text style={styles.compareLabel}>{row.label}</Text>
                <Text style={[styles.compareFree, compact && styles.compareColCompact]}>
                  {row.free}
                </Text>
                <Text style={[styles.comparePro, compact && styles.compareColCompact]}>
                  {row.pro}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerTop}>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel={`${APP_NAME} — back to top`}
              hitSlop={8}
              onPress={navigateToLanding}
              style={({ pressed }) => [styles.footerBrand, pressed && styles.pressed]}
            >
              <BrandMark size={24} />
              <Text style={styles.footerBrandText}>{APP_NAME}</Text>
            </Pressable>
            <View style={styles.footerLinks}>
              {showStoreBadges ? (
                <>
                  <FooterLink label="Android" href={PLAY_STORE_URL} />
                  {APP_STORE_URL ? (
                    <FooterLink label="iPhone" href={APP_STORE_URL} />
                  ) : (
                    <Text style={styles.footerSoon}>iPhone — coming soon</Text>
                  )}
                </>
              ) : null}
              <FooterLink label="Privacy" onPress={() => navigateToLegal('Privacy')} />
              <FooterLink label="Terms" onPress={() => navigateToLegal('Terms')} />
              <FooterLink label="Support" onPress={() => navigateToLegal('Support')} />
              <FooterLink label="Delete account" onPress={() => navigateToLegal('DeleteAccount')} />
              <FooterLink label="Explore" onPress={onExplore} />
            </View>
          </View>
          <SeoExploreLinks />
        </View>
      </ScrollView>
    </View>
  );
}

function LandingNav({
  compact,
  isAuthenticated,
  onAnchor,
  onSignIn,
  topInset,
}: {
  compact: boolean;
  isAuthenticated: boolean;
  onAnchor: (id: SectionId) => void;
  onSignIn: () => void;
  topInset: number;
}) {
  const brand = (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={`${APP_NAME} home`}
      hitSlop={8}
      onPress={navigateToLanding}
      style={({ pressed }) => [styles.navBrand, pressed && styles.pressed]}
    >
      <BrandMark size={24} />
      {compact ? null : <Text style={styles.navBrandText}>{APP_NAME}</Text>}
    </Pressable>
  );

  const anchors = (
    <View style={[styles.navAnchors, compact && styles.navAnchorsCompact]}>
      {LANDING_NAV_ANCHORS.map((anchor) => (
        <Pressable
          key={anchor.id}
          accessibilityRole="link"
          accessibilityLabel={anchor.label}
          {...(isWeb ? { href: landingHref(anchor.id) } : null)}
          onPress={(event) => {
            if (isWeb) {
              event.preventDefault?.();
            }
            onAnchor(anchor.id);
          }}
          hitSlop={4}
          style={({ pressed }) => [
            styles.navLink,
            compact && styles.navLinkCompact,
            pressed && styles.pressed,
          ]}
        >
          <Text style={[styles.navLinkText, compact && styles.navLinkTextCompact]}>
            {anchor.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );

  const ctas = (
    <View style={styles.navCtas}>
      <GlassButton variant="primary" size="sm" onPress={onSignIn}>
        {isAuthenticated ? 'Dashboard' : 'Sign in'}
      </GlassButton>
    </View>
  );

  if (compact) {
    return (
      <View style={[styles.navBar, { paddingTop: Math.max(topInset, 8) }]}>
        <View style={styles.navBarTop}>
          {brand}
          {ctas}
        </View>
        {anchors}
      </View>
    );
  }

  return (
    <View style={[styles.navOuter, { top: Math.max(topInset, 10) }]}>
      <View style={styles.nav}>
        {brand}
        {anchors}
        {ctas}
      </View>
    </View>
  );
}

function FooterLink({
  label,
  onPress,
  href,
}: {
  label: string;
  onPress?: () => void;
  href?: string;
}) {
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={label}
      onPress={() => {
        if (href) {
          openStoreUrl(href);
          return;
        }
        onPress?.();
      }}
      style={({ pressed }) => [styles.footerLink, pressed && styles.pressed]}
    >
      <Text style={styles.footerLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 72,
    paddingBottom: spacing.xl * 2,
    paddingHorizontal: spacing.lg,
    maxWidth: 1120,
    width: '100%',
    alignSelf: 'center',
    gap: spacing.xl,
  },
  scrollContentCompact: {
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  statusScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 19,
    backgroundColor: colors.background,
  },
  navBar: {
    zIndex: 20,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    gap: 2,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    backgroundColor: colors.background,
  },
  navBarTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 44,
    gap: spacing.md,
  },
  navOuter: {
    position: 'absolute',
    top: 10,
    left: 12,
    right: 12,
    zIndex: 20,
  } as ViewStyle,
  nav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 44,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.tabBarGlassBorder,
    backgroundColor: 'rgba(11, 15, 20, 0.78)',
    backdropFilter: 'blur(22px) saturate(160%)',
    WebkitBackdropFilter: 'blur(22px) saturate(160%)',
  } as ViewStyle,
  navBrand: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flexShrink: 0,
    minWidth: 44,
    minHeight: 44,
    cursor: 'pointer',
  } as ViewStyle,
  navBrandText: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
  },
  navAnchors: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    minWidth: 0,
  },
  navAnchorsCompact: {
    flex: 0,
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingBottom: 2,
  },
  navLink: {
    minHeight: 32,
    justifyContent: 'center',
    cursor: 'pointer',
  } as ViewStyle,
  navLinkCompact: {
    flex: 1,
    minHeight: 44,
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
  },
  navLinkText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
  },
  navLinkTextCompact: {
    textAlign: 'center',
  },
  navCtas: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  hero: {
    paddingTop: spacing.md,
    paddingBottom: spacing.lg,
    gap: spacing.md,
    maxWidth: 720,
    animationName: 'landingFadeUp',
    animationDuration: '0.55s',
    animationFillMode: 'both',
  } as ViewStyle,
  kicker: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  heroTitle: {
    fontSize: 52,
    lineHeight: 58,
    fontWeight: '700',
    color: colors.text,
    letterSpacing: -1.2,
  },
  heroTitleCompact: {
    fontSize: 36,
    lineHeight: 42,
  },
  heroBody: {
    ...typography.body,
    fontSize: 18,
    lineHeight: 26,
    color: colors.textMuted,
    maxWidth: 560,
  },
  heroBodyCompact: {
    fontSize: 16,
    lineHeight: 24,
  },
  heroPillars: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    maxWidth: 560,
  },
  heroPillar: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.text,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.35)',
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
  },
  heroCtas: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  exploreLink: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: spacing.sm,
    cursor: 'pointer',
  } as ViewStyle,
  exploreLinkText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
  },
  section: {
    gap: spacing.md,
    paddingTop: spacing.lg,
  },
  sectionEyebrow: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
  sectionTitle: {
    ...typography.title,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  sectionTitleCompact: {
    fontSize: 24,
    lineHeight: 30,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  gridTwo: {},
  gridOne: {},
  gridCell: {
    width: '100%',
    maxWidth: '100%',
    flexGrow: 1,
    flexBasis: 320,
    minWidth: 0,
  },
  gridCellFeatured: {
    flexBasis: '100%',
    maxWidth: '100%',
  },
  capCard: {
    gap: spacing.sm,
  },
  capCardCompact: {
    padding: spacing.md,
  },
  capHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  capTitle: {
    ...typography.headline,
    color: colors.text,
    flex: 1,
  },
  capLine: {
    ...typography.body,
    color: colors.text,
  },
  capWhy: {
    ...typography.caption,
    color: colors.textMuted,
    lineHeight: 18,
  },
  sectionLead: {
    ...typography.body,
    color: colors.textMuted,
    maxWidth: 560,
    marginBottom: spacing.sm,
  },
  compare: {
    marginTop: spacing.lg,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    overflow: 'hidden',
  },
  compareHead: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  compareHeadSpacer: {
    flex: 1,
  },
  compareHeadFree: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    width: 88,
    textAlign: 'right',
  },
  compareHeadPro: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
    width: 88,
    textAlign: 'right',
  },
  compareColCompact: {
    width: 64,
  },
  compareRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    gap: spacing.sm,
  },
  compareLabel: {
    ...typography.caption,
    color: colors.text,
    flex: 1,
  },
  compareFree: {
    ...typography.caption,
    color: colors.textMuted,
    width: 88,
    textAlign: 'right',
  },
  comparePro: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
    width: 88,
    textAlign: 'right',
  },
  proBadge: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '800',
    color: colors.accent,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.5)',
  },
  shotFrame: {
    marginTop: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(2, 6, 23, 0.45)',
    overflow: 'hidden',
    minWidth: 0,
  },
  howRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  howCol: {
    flexDirection: 'column',
  },
  howCard: {
    flex: 1,
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  howN: {
    ...typography.label,
    color: colors.accent,
  },
  howTitle: {
    ...typography.headline,
    color: colors.text,
  },
  howBody: {
    ...typography.body,
    color: colors.textMuted,
  },
  priceRow: {
    flexDirection: 'row',
    gap: spacing.md,
    alignItems: 'stretch',
  },
  priceCard: {
    flex: 1,
    gap: 8,
    minWidth: 0,
  },
  priceCardProWrap: {
    flex: 1,
    minWidth: 0,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.45)',
  },
  priceName: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  priceAmount: {
    ...typography.display,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  priceCadence: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },
  priceFeature: {
    ...typography.body,
    color: colors.text,
  },
  priceLimit: {
    ...typography.body,
    color: colors.textMuted,
  },
  priceCta: {
    marginTop: spacing.md,
  },
  footer: {
    marginTop: spacing.xl,
    paddingTop: spacing.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
    gap: spacing.md,
  },
  footerTop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  footerBrand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 44,
    minHeight: 44,
    cursor: 'pointer',
  } as ViewStyle,
  footerBrandText: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
  },
  footerLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  footerLink: {
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
    cursor: 'pointer',
  } as ViewStyle,
  footerLinkText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
  },
  footerSoon: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
    minHeight: 44,
    paddingHorizontal: spacing.xs,
    textAlignVertical: 'center',
  },
  pressed: {
    opacity: 0.85,
  },
});
